import { useEffect, useState } from "react"
import { useAuthStore, useResultsStore } from "@/store"
import { DashboardLayout } from "@/components/layout/dashboard-layout"
import { PageHeader } from "@/components/ui/page-header"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { EmptyState } from "@/components/ui/empty-state"
import { FileText, Printer, ChevronDown } from "lucide-react"
import { resolveMediaUrl } from "@/lib/utils"
import type {
  SingleTermReportCardDto,
  SingleMidTermReportCardDto,
  CumulativeReportCardDto,
} from "@/store/results-store"

/* ── Nigerian Grading System ── */
const GRADING_TABLE = [
  { grade: "A1", range: "75 – 100", remark: "Excellent" },
  { grade: "B2", range: "70 – 74", remark: "Very Good" },
  { grade: "B3", range: "65 – 69", remark: "Very Good" },
  { grade: "C4", range: "60 – 64", remark: "Good" },
  { grade: "C5", range: "55 – 59", remark: "Good" },
  { grade: "C6", range: "50 – 54", remark: "Good" },
  { grade: "D7", range: "45 – 49", remark: "Fair" },
  { grade: "E8", range: "40 – 44", remark: "Poor" },
  { grade: "F9", range: "01 – 39", remark: "Very Poor" },
]

const EFFECTIVENESS_ITEMS = [
  "Punctuality",
  "Neatness",
  "Honesty",
  "Cooperation with others",
  "Leadership",
  "Helping others",
  "Health",
  "Attitude to school Activities",
  "Attentiveness",
  "Speaking/Handwriting",
]

function termLabel(num: number): string {
  return num === 1 ? "1ST TERM" : num === 2 ? "2ND TERM" : "3RD TERM"
}

function formatPosition(pos: number): string {
  const s = ["th", "st", "nd", "rd"]
  const v = pos % 100
  return pos + (s[(v - 20) % 10] || s[v] || s[0])
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character] || character)
}

/* ════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════ */

export default function StudentReportCard() {
  const { user } = useAuthStore()
  const {
    reportCard,
    midTermReportCard,
    cumulativeReportCard,
    currentSession,
    academicSessions,
    isLoading,
    error,
    fetchSingleTermReportCard,
    fetchSingleMidTermReportCard,
    fetchCumulativeReportCard,
    fetchCurrentSession,
    fetchAcademicSessions,
    clearReportCard,
  } = useResultsStore()

  const [selectedTerm, setSelectedTerm] = useState<number>(1)
  const [selectedSession, setSelectedSession] = useState<string>("")
  const [viewMode, setViewMode] = useState<"single" | "midterm" | "cumulative">("single")

  useEffect(() => {
    fetchCurrentSession()
    fetchAcademicSessions()
    return () => clearReportCard()
  }, [])

  useEffect(() => {
    if (currentSession) {
      setSelectedSession(currentSession.name)
      setSelectedTerm(currentSession.currentTerm || 1)
    }
  }, [currentSession])

  const handleFetch = () => {
    if (!user?.id || !selectedSession) return
    clearReportCard()
    if (viewMode === "single") {
      fetchSingleTermReportCard(user.id, selectedTerm, selectedSession)
    } else if (viewMode === "midterm") {
      fetchSingleMidTermReportCard(user.id, selectedTerm, selectedSession)
    } else if (viewMode === "cumulative" && selectedTerm === 3) {
      fetchCumulativeReportCard(user.id, selectedSession)
    }
  }

  const handlePrint = () => {
    const card = viewMode === "single" ? reportCard : viewMode === "midterm" ? midTermReportCard : cumulativeReportCard
    if (!card) return
    const pw = window.open("", "_blank")
    if (!pw) return
    const html =
      viewMode === "single"
        ? buildSingleTermPrintHtml(reportCard!, selectedTerm)
        : viewMode === "midterm"
          ? buildMidTermPrintHtml(midTermReportCard!)
          : buildCumulativePrintHtml(cumulativeReportCard!)
    pw.document.write(html)
    pw.document.close()
    pw.focus()
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </DashboardLayout>
    )
  }

  const activeCard = viewMode === "single" ? reportCard : viewMode === "midterm" ? midTermReportCard : cumulativeReportCard

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Report Card"
          description="View and print your academic report card"
          actions={
            activeCard && (
              <Button onClick={handlePrint} variant="outline">
                <Printer className="h-4 w-4 mr-2" />
                Print Report Card
              </Button>
            )
          }
        />

        {/* ── Controls ── */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                <div className="relative">
                  <select
                    value={viewMode}
                    onChange={(e) => {
                      setViewMode(e.target.value as "single" | "midterm" | "cumulative")
                      clearReportCard()
                    }}
                    className="w-full appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="single">Single Term</option>
                    <option value="midterm">Mid-term</option>
                    <option value="cumulative">Cumulative (Annual)</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                <div className="relative">
                  <select
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                    className="w-full appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select session</option>
                    {academicSessions.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {(viewMode === "single" || viewMode === "midterm" || viewMode === "cumulative") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                  <div className="relative">
                    <select
                      value={selectedTerm}
                      onChange={(e) => setSelectedTerm(Number(e.target.value))}
                      className="w-full appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={1}>First Term</option>
                      <option value={2}>Second Term</option>
                      <option value={3}>Third Term</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              <div>
                <Button onClick={handleFetch} disabled={isLoading || !selectedSession || (viewMode === "cumulative" && selectedTerm !== 3)} className="w-full">
                  {isLoading ? "Loading..." : "View Report Card"}
                </Button>
              </div>
            </div>
            {viewMode === "cumulative" && selectedTerm !== 3 && (
              <p className="mt-3 text-sm text-amber-700">Cumulative report cards are available for the third term only.</p>
            )}
          </CardContent>
        </Card>

        {error && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-red-600 text-sm">{error}</div>
            </CardContent>
          </Card>
        )}

        {isLoading && (
          <div className="flex items-center justify-center h-48">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!isLoading && !error && viewMode === "single" && reportCard && (
          <SingleTermReportCardView
            data={reportCard}
            termNum={selectedTerm}
          />
        )}

        {!isLoading && !error && viewMode === "midterm" && midTermReportCard && (
          <MidTermReportCardView data={midTermReportCard} />
        )}

        {!isLoading && !error && viewMode === "cumulative" && cumulativeReportCard && (
          <CumulativeReportCardView
            data={cumulativeReportCard}
          />
        )}

        {!isLoading && !error && !activeCard && (
          <EmptyState
            icon={FileText}
            title="No Report Card"
            description="Select a session and term, then click 'View Report Card' to generate your report."
          />
        )}
      </div>
    </DashboardLayout>
  )
}

/* ════════════════════════════════════════
   SINGLE TERM — ON-SCREEN VIEW
   ════════════════════════════════════════ */

function SingleTermReportCardView({
  data,
  termNum,
}: {
  data: SingleTermReportCardDto
  termNum: number
}) {
  const caTotal = (s: { firstCA: number; secondCA: number; thirdCA: number }) =>
    s.firstCA + s.secondCA + s.thirdCA

  return (
    <Card className="overflow-hidden border-0 shadow-lg">
      <CardContent className="p-0">
        {/* ── School Header (Flex Layout) ── */}
        <div className="bg-blue-900 text-white flex items-center justify-between gap-4 px-6 py-4">
          {/* Left: School Logo */}
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.schoolLogo && (
              <img
                src={resolveMediaUrl(data.schoolLogo)}
                alt="logo"
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* Center: School Name & Subtitle */}
          <div className="flex-1 text-center">
            <h1 className="text-2xl font-extrabold uppercase tracking-wide leading-tight">
              {data.schoolName}
            </h1>
            <p className="text-blue-100 text-xs mt-1 uppercase tracking-wider">
              Report Sheet
            </p>
          </div>

          {/* Right: Student Photo */}
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.studentPhotoUrl ? (
              <img
                src={resolveMediaUrl(data.studentPhotoUrl)}
                alt={`${data.studentName} profile`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <div className="text-[8px] text-gray-400 text-center">No photo</div>
            )}
          </div>
        </div>

        {/* ── Student Info Row 1 ── */}
        <div className="border border-gray-300 text-xs">
          <div className="flex divide-x divide-gray-300">
            <InfoCell label="STUDENT'S NAME" value={data.studentName} className="flex-[2]" />
            <InfoCell label="SESSION" value={data.session} className="flex-1" />
            <InfoCell label="TERM" value={termLabel(termNum)} className="flex-1" />
            <InfoCell label="CLASS" value={data.className} className="flex-1" />
            <InfoCell label="PERFORMANCE" value={`${data.percentage.toFixed(1)}%`} className="flex-1" />
          </div>
          {/* Row 2 */}
          <div className="flex divide-x divide-gray-300 border-t border-gray-300">
            <InfoCell label="NO. IN CLASS" value={String(data.totalStudents)} className="flex-1" />
            <InfoCell label="POSITION" value={formatPosition(data.position)} className="flex-1" />
            <InfoCell label="GENDER" value={data.gender} className="flex-1" />
            <InfoCell label="STUDENT ID" value={data.studentUin} className="flex-[2]" />
          </div>
        </div>

        {/* ── Attendance + Weight/Height ── */}
        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          {/* Attendance */}
          <div className="border-r border-gray-300">
            <SectionBanner>Attendance</SectionBanner>
            <InfoRow label="No. of Times School Opened" value="" />
            <InfoRow label="No. of Times Present" value="" />
            <InfoRow label="No. of Times Punctual" value="" />
          </div>
          {/* Weight / Height */}
          <div>
            <SectionBanner>Weight / Height</SectionBanner>
            <InfoRow
              label="Expected Date of Resumption"
              value={
                data.nextTermBeginsOn
                  ? new Date(data.nextTermBeginsOn).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : ""
              }
            />
            <InfoRow label="Height at the end of term" value="" />
            <InfoRow label="Weight at the beginning of term" value="" />
            <InfoRow label="Weight at the end of term" value="" />
          </div>
        </div>

        {/* ── Assessment Table ── */}
        <div className="border-x border-gray-300">
          <SectionBanner>Assessment</SectionBanner>
        </div>

        <div className="overflow-x-auto border border-gray-300">
          <table className="w-full text-xs border-collapse min-w-[700px]">
            <thead>
              <tr className="bg-blue-900 text-white text-[10px]">
                <th className="border border-blue-800 px-2 py-2 text-left font-semibold">
                  SUBJECTS
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-14">
                  Marks<br />Obtainable
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-14">
                  Continues<br />Assessment
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-14">
                  Exam<br />Score
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-16">
                  {termLabel(termNum)}<br />Score (%)
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-12">
                  Grade
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-20">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {data.subjects.map((subj, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-white" : "bg-blue-50/40"}
                >
                  <td className="border border-gray-300 px-2 py-1.5 font-medium uppercase">
                    {subj.subjectName}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    100
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    {caTotal(subj)}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    {subj.exam}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center font-semibold">
                    {subj.totalScore}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center font-bold">
                    {subj.grade}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center text-[10px]">
                    {subj.remark}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Grading Method + Effectiveness ── */}
        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          {/* Grading */}
          <div className="border-r border-gray-300 p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">
              Grading Method
            </h4>
            <table className="w-full text-[10px]">
              <tbody>
                {GRADING_TABLE.map((g) => (
                  <tr
                    key={g.grade}
                    className="border-b border-gray-200 last:border-0"
                  >
                    <td className="py-0.5 font-semibold w-24">
                      {g.grade} = {g.range}
                    </td>
                    <td className="py-0.5 pl-2">{g.remark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Effectiveness */}
          <div className="p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">
              Effectiveness
            </h4>
            <table className="w-full text-[10px]">
              <thead>
                <tr>
                  <th className="text-left py-0.5"></th>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <th
                      key={n}
                      className="text-center py-0.5 w-6 font-semibold"
                    >
                      {n}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EFFECTIVENESS_ITEMS.map((item) => (
                  <tr
                    key={item}
                    className="border-b border-gray-200 last:border-0"
                  >
                    <td className="py-0.5">{item}</td>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <td key={n} className="text-center py-0.5">
                        <div className="w-3.5 h-3.5 border border-gray-300 rounded-sm mx-auto" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Class Teacher's Comment ── */}
        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-end gap-3">
            <span className="font-bold whitespace-nowrap">
              Class Teacher's Comment:
            </span>
            <span className="flex-1 border-b border-gray-400 min-h-[1.2em] italic text-gray-700 whitespace-pre-wrap">{data.classTeacherComment || ""}</span>
            <span className="font-bold whitespace-nowrap ml-4">Signature:</span>
            <span className="flex h-7 w-28 items-end justify-center border-b border-gray-400">{data.classTeacherSignatureUrl ? <img src={resolveMediaUrl(data.classTeacherSignatureUrl)} alt="Class teacher signature" className="max-h-7 max-w-full object-contain" /> : null}</span>
          </div>
        </div>

        {/* ── Principal's Comment ── */}
        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-start gap-2">
            <span className="font-bold whitespace-nowrap">
              Principal's Comment:
            </span>
            <span className="flex-1 italic text-gray-700">
              {data.principalRemark}
            </span>
          </div>
          <div className="border-b border-gray-400 mt-2" />
        </div>

        {/* ── Signatures (Side-by-Side) ── */}
        <div className="flex justify-between gap-6 border border-gray-300 border-t-0 px-4 py-3 text-[10px]">
          {/* Principal Signature */}
          <div className="flex-1">
            <div className="h-12 border-b-2 border-gray-400 flex items-end justify-start pb-1 mb-1">
              {data.principalSignatureUrl ? (
                <img
                  src={resolveMediaUrl(data.principalSignatureUrl)}
                  alt="Principal signature"
                  className="max-h-10 max-w-full object-contain"
                />
              ) : null}
            </div>
            <p className="font-bold italic text-xs">Principal</p>
            <p className="font-bold uppercase text-[9px]">{data.schoolName}</p>
          </div>

          {/* Student Signature */}
          <div className="flex-1 text-right">
            <div className="h-12 border-b-2 border-gray-400 flex items-end justify-end pb-1 mb-1">
              {data.studentSignatureUrl ? (
                <img
                  src={resolveMediaUrl(data.studentSignatureUrl)}
                  alt="Student signature"
                  className="max-h-10 max-w-full object-contain"
                />
              ) : null}
            </div>
            <p className="font-bold italic text-xs">Student</p>
            <p className="font-bold uppercase text-[9px]">{data.studentName}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/* ════════════════════════════════════════
   CUMULATIVE — ON-SCREEN VIEW
   ════════════════════════════════════════ */

function MidTermReportCardView({ data }: { data: SingleMidTermReportCardDto }) {
  const termName = data.term.toLowerCase() === "first"
    ? "1ST TERM"
    : data.term.toLowerCase() === "second"
      ? "2ND TERM"
      : "3RD TERM"
  const resumeDate = data.nextTermBeginsOn
    ? new Date(data.nextTermBeginsOn).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : ""

  return (
    <Card className="overflow-hidden border-0 shadow-lg">
      <CardContent className="p-0">
        <div className="bg-blue-900 text-white flex items-center justify-between gap-4 px-6 py-4">
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.schoolLogo && <img src={resolveMediaUrl(data.schoolLogo)} alt="logo" className="w-full h-full object-contain" />}
          </div>
          <div className="flex-1 text-center">
            <h1 className="text-2xl font-extrabold uppercase tracking-wide leading-tight">{data.schoolName}</h1>
            <p className="text-blue-100 text-xs mt-1 uppercase tracking-wider">Report Sheet</p>
          </div>
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.studentPhotoUrl ? <img src={resolveMediaUrl(data.studentPhotoUrl)} alt={`${data.studentName} profile`} className="w-full h-full object-cover rounded-full" /> : <div className="text-[8px] text-gray-400 text-center">No photo</div>}
          </div>
        </div>

        <div className="border border-gray-300 text-xs">
          <div className="flex divide-x divide-gray-300">
            <InfoCell label="STUDENT'S NAME" value={data.studentName} className="flex-[2]" />
            <InfoCell label="SESSION" value={data.session} className="flex-1" />
            <InfoCell label="TERM" value={termName} className="flex-1" />
            <InfoCell label="CLASS" value={data.className} className="flex-1" />
            <InfoCell label="PERFORMANCE" value={`${data.percentage.toFixed(1)}%`} className="flex-1" />
          </div>
          <div className="flex divide-x divide-gray-300 border-t border-gray-300">
            <InfoCell label="NO. IN CLASS" value={String(data.totalStudents)} className="flex-1" />
            <InfoCell label="POSITION" value={formatPosition(data.position)} className="flex-1" />
            <InfoCell label="GENDER" value={data.gender} className="flex-1" />
            <InfoCell label="STUDENT ID" value={data.studentUin} className="flex-[2]" />
          </div>
        </div>

        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          <div className="border-r border-gray-300">
            <SectionBanner>Attendance</SectionBanner>
            <InfoRow label="No. of Times School Opened" value="" />
            <InfoRow label="No. of Times Present" value="" />
            <InfoRow label="No. of Times Punctual" value="" />
          </div>
          <div>
            <SectionBanner>Weight / Height</SectionBanner>
            <InfoRow label="Expected Date of Resumption" value={resumeDate} />
            <InfoRow label="Height at the end of term" value="" />
            <InfoRow label="Weight at the beginning of term" value="" />
            <InfoRow label="Weight at the end of term" value="" />
          </div>
        </div>

        <div className="border-x border-gray-300"><SectionBanner>Mid-term Assessment - Continuous Assessment Only</SectionBanner></div>
        <div className="overflow-x-auto border border-gray-300">
          <table className="w-full text-xs border-collapse min-w-[700px]">
            <thead><tr className="bg-blue-900 text-white text-[10px]">
              <th className="border border-blue-800 px-2 py-2 text-left font-semibold">SUBJECTS</th>
              <th className="border border-blue-800 px-1 py-2 text-center font-semibold">1st CA</th>
              <th className="border border-blue-800 px-1 py-2 text-center font-semibold">2nd CA</th>
              <th className="border border-blue-800 px-1 py-2 text-center font-semibold">3rd CA</th>
              <th className="border border-blue-800 px-1 py-2 text-center font-semibold">CA Total</th>
            </tr></thead>
            <tbody>{data.subjects.map((subject, index) => (
              <tr key={subject.subjectCode} className={index % 2 === 0 ? "bg-white" : "bg-blue-50/40"}>
                <td className="border border-gray-300 px-2 py-1.5 font-medium uppercase">{subject.subjectName}</td>
                <td className="border border-gray-300 px-1 py-1.5 text-center">{subject.first_CA_Score}</td>
                <td className="border border-gray-300 px-1 py-1.5 text-center">{subject.second_CA_Score}</td>
                <td className="border border-gray-300 px-1 py-1.5 text-center">{subject.third_CA_Score}</td>
                <td className="border border-gray-300 px-1 py-1.5 text-center font-semibold">{subject.continuousAssessmentTotal}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          <div className="border-r border-gray-300 p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">Grading Method</h4>
            <table className="w-full text-[10px]"><tbody>{GRADING_TABLE.map((grade) => (
              <tr key={grade.grade} className="border-b border-gray-200 last:border-0">
                <td className="py-0.5 font-semibold w-24">{grade.grade} = {grade.range}</td><td className="py-0.5 pl-2">{grade.remark}</td>
              </tr>
            ))}</tbody></table>
          </div>
          <div className="p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">Effectiveness</h4>
            <table className="w-full text-[10px]"><thead><tr><th className="text-left py-0.5" />{[5, 4, 3, 2, 1].map((score) => <th key={score} className="text-center py-0.5 w-6 font-semibold">{score}</th>)}</tr></thead>
              <tbody>{EFFECTIVENESS_ITEMS.map((item) => <tr key={item} className="border-b border-gray-200 last:border-0"><td className="py-0.5">{item}</td>{[5, 4, 3, 2, 1].map((score) => <td key={score} className="text-center py-0.5"><div className="w-3.5 h-3.5 border border-gray-300 rounded-sm mx-auto" /></td>)}</tr>)}</tbody>
            </table>
          </div>
        </div>

        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-end gap-3"><span className="font-bold whitespace-nowrap">Class Teacher's Comment:</span><span className="flex-1 border-b border-gray-400 min-h-[1.2em] italic text-gray-700 whitespace-pre-wrap">{data.classTeacherComment || ""}</span><span className="font-bold whitespace-nowrap ml-4">Signature:</span><span className="flex h-7 w-28 items-end justify-center border-b border-gray-400">{data.classTeacherSignatureUrl && <img src={resolveMediaUrl(data.classTeacherSignatureUrl)} alt="Class teacher signature" className="max-h-7 max-w-full object-contain" />}</span></div>
        </div>
        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-start gap-2"><span className="font-bold whitespace-nowrap">Principal's Comment:</span><span className="flex-1 italic text-gray-700">{data.principalRemark}</span></div>
          <div className="border-b border-gray-400 mt-2" />
        </div>
        <div className="flex justify-between gap-6 border border-gray-300 border-t-0 px-4 py-3 text-[10px]">
          <div className="flex-1"><div className="h-12 border-b-2 border-gray-400 flex items-end justify-start pb-1 mb-1">{data.principalSignatureUrl && <img src={resolveMediaUrl(data.principalSignatureUrl)} alt="Principal signature" className="max-h-10 max-w-full object-contain" />}</div><p className="font-bold italic text-xs">Principal</p><p className="font-bold uppercase text-[9px]">{data.schoolName}</p></div>
          <div className="flex-1 text-right"><div className="h-12 border-b-2 border-gray-400 flex items-end justify-end pb-1 mb-1">{data.studentSignatureUrl && <img src={resolveMediaUrl(data.studentSignatureUrl)} alt="Student signature" className="max-h-10 max-w-full object-contain" />}</div><p className="font-bold italic text-xs">Student</p><p className="font-bold uppercase text-[9px]">{data.studentName}</p></div>
        </div>
      </CardContent>
    </Card>
  )
}
function CumulativeReportCardView({
  data,
}: {
  data: CumulativeReportCardDto
}) {
  return (
    <Card className="overflow-hidden border-0 shadow-lg">
      <CardContent className="p-0">
        {/* ── School Header (Flex Layout) ── */}
        <div className="bg-blue-900 text-white flex items-center justify-between gap-4 px-6 py-4">
          {/* Left: School Logo */}
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.schoolLogo && (
              <img
                src={resolveMediaUrl(data.schoolLogo)}
                alt="logo"
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* Center: School Name & Subtitle */}
          <div className="flex-1 text-center">
            <h1 className="text-2xl font-extrabold uppercase tracking-wide leading-tight">
              {data.schoolName}
            </h1>
            <p className="text-blue-100 text-xs mt-1 uppercase tracking-wider">
              Cumulative Report Sheet — {data.session}
            </p>
          </div>

          {/* Right: Student Photo */}
          <div className="w-14 h-14 rounded-full bg-white p-1 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {data.studentPhotoUrl ? (
              <img
                src={resolveMediaUrl(data.studentPhotoUrl)}
                alt={`${data.studentName} profile`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <div className="text-[8px] text-gray-400 text-center">No photo</div>
            )}
          </div>
        </div>

        {/* ── Student Info ── */}
        <div className="border border-gray-300 text-xs">
          <div className="flex divide-x divide-gray-300">
            <InfoCell label="STUDENT'S NAME" value={data.studentName} className="flex-[2]" />
            <InfoCell label="SESSION" value={data.session} className="flex-1" />
            <InfoCell label="CLASS" value={data.className} className="flex-1" />
            <InfoCell label="PERFORMANCE" value={`${data.cumulativePercentage.toFixed(1)}%`} className="flex-1" />
            <InfoCell label="POSITION" value={formatPosition(data.cumulativePosition)} className="flex-1" />
          </div>
          <div className="flex divide-x divide-gray-300 border-t border-gray-300">
            <InfoCell label="STUDENT ID" value={data.studentUin} className="flex-[2]" />
            <InfoCell label="GENDER" value={data.gender} className="flex-1" />
            <InfoCell label="NO. IN CLASS" value={String(data.totalStudents)} className="flex-1" />
            <div className="flex-[2]" />
          </div>
        </div>

        {/* ── Attendance + Weight/Height ── */}
        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          <div className="border-r border-gray-300">
            <SectionBanner>Attendance</SectionBanner>
            <InfoRow label="No. of Times School Opened" value="" />
            <InfoRow label="No. of Times Present" value="" />
            <InfoRow label="No. of Times Punctual" value="" />
          </div>
          <div>
            <SectionBanner>Weight / Height</SectionBanner>
            <InfoRow
              label="Expected Date of Resumption"
              value={
                data.nextTermBeginsOn
                  ? new Date(data.nextTermBeginsOn).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : ""
              }
            />
            <InfoRow label="Height at the end of term" value="" />
            <InfoRow label="Weight at the beginning of term" value="" />
            <InfoRow label="Weight at the end of term" value="" />
          </div>
        </div>

        {/* ── Assessment ── */}
        <div className="border-x border-gray-300">
          <SectionBanner>Assessment — Cumulative</SectionBanner>
        </div>

        <div className="overflow-x-auto border border-gray-300">
          <table className="w-full text-xs border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-blue-900 text-white text-[10px]">
                <th className="border border-blue-800 px-2 py-2 text-left font-semibold">
                  SUBJECTS
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-16">
                  1st Term<br />Total
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-16">
                  2nd Term<br />Total
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-16">
                  3rd Term<br />Total
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-14">
                  Average
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-12">
                  Grade
                </th>
                <th className="border border-blue-800 px-1 py-2 text-center font-semibold w-20">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {data.subjects.map((subj, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-white" : "bg-blue-50/40"}
                >
                  <td className="border border-gray-300 px-2 py-1.5 font-medium uppercase">
                    {subj.subjectName}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    {subj.firstTermTotal || "—"}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    {subj.secondTermTotal || "—"}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center">
                    {subj.thirdTermTotal || "—"}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center font-semibold">
                    {subj.averageScore.toFixed(1)}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center font-bold">
                    {subj.grade}
                  </td>
                  <td className="border border-gray-300 px-1 py-1.5 text-center text-[10px]">
                    {subj.remark}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Grading + Effectiveness ── */}
        <div className="grid grid-cols-2 text-xs border-x border-b border-gray-300">
          <div className="border-r border-gray-300 p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">
              Grading Method
            </h4>
            <table className="w-full text-[10px]">
              <tbody>
                {GRADING_TABLE.map((g) => (
                  <tr key={g.grade} className="border-b border-gray-200 last:border-0">
                    <td className="py-0.5 font-semibold w-24">
                      {g.grade} = {g.range}
                    </td>
                    <td className="py-0.5 pl-2">{g.remark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3">
            <h4 className="font-bold uppercase text-[10px] mb-2 text-blue-900">
              Effectiveness
            </h4>
            <table className="w-full text-[10px]">
              <thead>
                <tr>
                  <th className="text-left py-0.5"></th>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <th key={n} className="text-center py-0.5 w-6 font-semibold">
                      {n}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EFFECTIVENESS_ITEMS.map((item) => (
                  <tr key={item} className="border-b border-gray-200 last:border-0">
                    <td className="py-0.5">{item}</td>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <td key={n} className="text-center py-0.5">
                        <div className="w-3.5 h-3.5 border border-gray-300 rounded-sm mx-auto" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Teacher + Principal Comment ── */}
        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-end gap-3">
            <span className="font-bold whitespace-nowrap">Class Teacher's Comment:</span>
            <span className="flex-1 border-b border-gray-400 min-h-[1.2em] italic text-gray-700 whitespace-pre-wrap">{data.classTeacherComment || ""}</span>
            <span className="font-bold whitespace-nowrap ml-4">Signature:</span>
            <span className="flex h-7 w-28 items-end justify-center border-b border-gray-400">{data.classTeacherSignatureUrl ? <img src={resolveMediaUrl(data.classTeacherSignatureUrl)} alt="Class teacher signature" className="max-h-7 max-w-full object-contain" /> : null}</span>
          </div>
        </div>
        <div className="border-x border-b border-gray-300 px-4 py-3 text-xs">
          <div className="flex items-start gap-2">
            <span className="font-bold whitespace-nowrap">Principal's Comment:</span>
            <span className="flex-1 italic text-gray-700">{data.principalRemark}</span>
          </div>
          <div className="border-b border-gray-400 mt-2" />
        </div>

        {/* ── Signatures (Side-by-Side) ── */}
        <div className="flex justify-between gap-6 border border-gray-300 border-t-0 px-4 py-3 text-[10px]">
          {/* Principal Signature */}
          <div className="flex-1">
            <div className="h-6 border-b-2 border-gray-400 flex items-end justify-start pb-0.5 mb-0.5">
              {data.principalSignatureUrl ? (
                <img
                  src={resolveMediaUrl(data.principalSignatureUrl)}
                  alt="Principal signature"
                  className="max-h-5 max-w-full object-contain"
                />
              ) : null}
            </div>
            <p className="font-bold italic text-xs">Principal</p>
            <p className="font-bold uppercase text-[9px]">{data.schoolName}</p>
          </div>

          {/* Student Signature */}
          <div className="flex-1 text-right">
            <div className="h-6 border-b-2 border-gray-400 flex items-end justify-end pb-0.5 mb-0.5">
              {data.studentSignatureUrl ? (
                <img
                  src={resolveMediaUrl(data.studentSignatureUrl)}
                  alt="Student signature"
                  className="max-h-5 max-w-full object-contain"
                />
              ) : null}
            </div>
            <p className="font-bold italic text-xs">Student</p>
            <p className="font-bold uppercase text-[9px]">{data.studentName}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/* ════════════════════════════════════════
   SHARED SMALL COMPONENTS
   ════════════════════════════════════════ */

function SectionBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-blue-900 text-white font-bold px-2 py-1 uppercase text-[10px] tracking-wide">
      {children}
    </div>
  )
}

function InfoCell({
  label,
  value,
  className = "",
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={`px-2 py-1.5 ${className}`}>
      <span className="font-bold">{label}:</span>{" "}
      <span>{value}</span>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-2 divide-x divide-gray-300 border-b border-gray-300 last:border-b-0">
      <div className="px-2 py-1.5 font-medium">{label}</div>
      <div className="px-2 py-1.5 text-center">{value}</div>
    </div>
  )
}

/* ════════════════════════════════════════
   PRINT HTML STYLES (shared)
   ════════════════════════════════════════ */

const PRINT_STYLES = `
*{margin:0;padding:0;box-sizing:border-box}
.page{width:100%}
body{font-family:'Segoe UI',Arial,sans-serif;padding:6px;color:#333;font-size:10px;line-height:1.15}
.header{display:flex;align-items:center;justify-content:space-between;gap:12px;background:__PRIMARY__;color:#fff;padding:12px 14px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.header .logo{width:54px;height:54px;flex:0 0 54px;border-radius:50%;background:#fff;padding:4px;display:flex;align-items:center;justify-content:center;overflow:hidden}
.header .logo img{width:100%;height:100%;object-fit:contain}
.header .meta{flex:1;text-align:center}
.header .student-photo{width:84px;height:102px;flex:0 0 84px;border-radius:8px;background:#fff;padding:3px;display:flex;align-items:center;justify-content:center;overflow:hidden}
.header .student-photo img{width:100%;height:100%;object-fit:cover;border-radius:6px}
.header h1{font-size:22px;text-transform:uppercase;letter-spacing:1.5px;font-weight:800}
.header .sub{font-size:10px;color:#d9e8f4;margin-top:3px;text-transform:uppercase;letter-spacing:2px}
table{width:100%;border-collapse:collapse}
td,th{border:1px solid #bbb;padding:4px 6px}
.c{text-align:center}
.b{font-weight:700}
.subj{font-weight:500;text-transform:uppercase}
.info td{font-size:11px;padding:3px 6px}
.lbl{font-weight:700}
.sh{background:__PRIMARY__;color:#fff;font-weight:700;font-size:10px;text-transform:uppercase;padding:4px 6px;letter-spacing:0.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.at th{background:__PRIMARY__;color:#fff;font-size:10px;padding:5px 4px;font-weight:600;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.at td{font-size:11px}
.at tr:nth-child(even){background:#f0f5fa;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.bg{display:grid;grid-template-columns:1fr 1fr;border:1px solid #bbb;border-top:0}
.bg>div{padding:6px}
.bg>div:first-child{border-right:1px solid #bbb}
.bg h4{font-size:10px;text-transform:uppercase;font-weight:700;margin-bottom:4px;color:__PRIMARY__}
.bg table{border:none}
.bg td,.bg th{border:none;border-bottom:1px solid #eee;padding:2px 4px;font-size:10px}
.bx{width:12px;height:12px;border:1px solid #999;display:inline-block;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.cm{border:1px solid #bbb;border-top:0;padding:5px 8px;font-size:10px}
.so{border:1px solid #bbb;border-top:0;padding:8px 8px;text-align:right;font-size:10px}
.so .in{display:inline-block;text-align:center}
.so .ln{width:150px;border-bottom:1px solid #666;margin-bottom:2px}
/* Signature grid: place two signature blocks side-by-side, left and right */
.siggrid{display:flex;justify-content:space-between;gap:22px;border:1px solid #bbb;border-top:0;padding:8px 10px;font-size:10px}
.siggrid .in{flex:1}
.siggrid .in:first-child{text-align:left !important}
.siggrid .in:last-child{text-align:right !important}
            .sigbox{height:23px;border-bottom:2px solid #666;display:flex;align-items:flex-end;justify-content:flex-start !important;padding-bottom:2px}
.siggrid .in:last-child .sigbox{justify-content:flex-end !important}
            .sigbox img{max-height:21px;max-width:100%;object-fit:contain}
@media print{
  @page{margin:6mm}
  body{padding:0}
  .page{page-break-inside:avoid;break-inside:avoid}
}
`

/* ════════════════════════════════════════
   PRINT HTML — SINGLE TERM
   ════════════════════════════════════════ */

function buildMidTermPrintHtml(data: SingleMidTermReportCardDto): string {
  const termName = data.term.toLowerCase() === 'first' ? '1ST TERM' : data.term.toLowerCase() === 'second' ? '2ND TERM' : '3RD TERM'
  const primary = (typeof window !== 'undefined') ? getComputedStyle(document.documentElement).getPropertyValue('--primary') || '#1e3a5f' : '#1e3a5f'
  const styles = PRINT_STYLES.replace(/__PRIMARY__/g, primary.trim())
  const rows = data.subjects.map(subject => `<tr><td class="subj">${subject.subjectName.toUpperCase()}</td><td class="c">${subject.first_CA_Score}</td><td class="c">${subject.second_CA_Score}</td><td class="c">${subject.third_CA_Score}</td><td class="c b">${subject.continuousAssessmentTotal}</td></tr>`).join('')
  const gradingRows = GRADING_TABLE.map(grade => `<tr><td style="font-weight:600">${grade.grade} = ${grade.range}</td><td>${grade.remark}</td></tr>`).join('')
  const effectivenessRows = EFFECTIVENESS_ITEMS.map(item => `<tr><td>${item}</td>${[5, 4, 3, 2, 1].map(() => `<td class="c"><div class="bx"></div></td>`).join('')}</tr>`).join('')
  const resumeDate = data.nextTermBeginsOn
    ? new Date(data.nextTermBeginsOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : ''

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Mid-term Report — ${data.studentName}</title><style>${styles}</style></head><body>
<div class="page">
  <div class="header">
    <div class="logo">${data.schoolLogo ? `<img src="${resolveMediaUrl(data.schoolLogo)}" alt="${data.schoolName} logo" />` : ''}</div>
    <div class="meta"><h1>${data.schoolName}</h1><div class="sub">Report Sheet</div></div>
    <div class="student-photo">${data.studentPhotoUrl ? `<img src="${resolveMediaUrl(data.studentPhotoUrl)}" alt="${data.studentName} profile" />` : '<span style="font-size:9px;color:#666;text-align:center;line-height:1.2;padding:4px">No photo uploaded</span>'}</div>
  </div>
  <table class="info">
    <tr><td><span class="lbl">STUDENT'S NAME:</span> ${data.studentName}</td><td><span class="lbl">SESSION:</span> ${data.session}</td><td><span class="lbl">TERM:</span> ${termName}</td><td><span class="lbl">CLASS:</span> ${data.className}</td><td><span class="lbl">PERFORMANCE:</span> ${data.percentage.toFixed(1)}%</td></tr>
    <tr><td><span class="lbl">NO. IN CLASS:</span> ${data.totalStudents}</td><td><span class="lbl">POSITION:</span> ${formatPosition(data.position)}</td><td><span class="lbl">GENDER:</span> ${data.gender}</td><td colspan="2"><span class="lbl">STUDENT ID:</span> ${data.studentUin}</td></tr>
  </table>
  <table><tr><td class="sh" style="width:50%">Attendance</td><td class="sh">Weight / Height</td></tr></table>
  <table>
    <tr><td style="width:25%">No. of Times School Opened</td><td style="width:25%"></td><td style="width:25%">Expected Date of Resumption</td><td style="width:25%">${resumeDate}</td></tr>
    <tr><td>No. of Times Present</td><td></td><td>Height at the end of term</td><td></td></tr>
    <tr><td>No. of Times Punctual</td><td></td><td>Weight at the beginning of term</td><td></td></tr>
    <tr><td></td><td></td><td>Weight at the end of term</td><td></td></tr>
  </table>
  <table><tr><td class="sh" colspan="5">Mid-term Assessment — Continuous Assessment Only</td></tr></table>
  <table class="at"><thead><tr><th style="text-align:left">SUBJECTS</th><th>1st CA</th><th>2nd CA</th><th>3rd CA</th><th>CA Total</th></tr></thead><tbody>${rows}</tbody></table>
  <div class="bg">
    <div><h4>Grading Method</h4><table>${gradingRows}</table></div>
    <div><h4>Effectiveness</h4><table><tr><th></th><th class="c">5</th><th class="c">4</th><th class="c">3</th><th class="c">2</th><th class="c">1</th></tr>${effectivenessRows}</table></div>
  </div>
  <div class="cm"><span class="lbl">Class Teacher's Comment:</span> <em style="white-space:pre-wrap">${escapeHtml(data.classTeacherComment || '')}</em> ______________________________________________ &nbsp;&nbsp;<span class="lbl">Signature:</span> <span style="display:inline-flex;width:72px;height:22px;align-items:flex-end;justify-content:center;border-bottom:1px solid #999">${data.classTeacherSignatureUrl ? `<img src="${resolveMediaUrl(data.classTeacherSignatureUrl)}" style="max-height:21px;max-width:100%;object-fit:contain" alt="Class teacher signature" />` : ''}</span></div>
  <div class="cm"><span class="lbl">Principal's Comment:</span> <em>${data.principalRemark}</em></div>
  <div class="cm" style="border-bottom:1px solid #bbb"></div>
  <div class="siggrid">
    <div class="in"><div class="sigbox">${data.principalSignatureUrl ? `<img src="${resolveMediaUrl(data.principalSignatureUrl)}" alt="Principal signature" />` : ''}</div><div><strong><em>Principal</em></strong></div><div><strong>${data.schoolName.toUpperCase()}</strong></div></div>
    <div class="in"><div class="sigbox">${data.studentSignatureUrl ? `<img src="${resolveMediaUrl(data.studentSignatureUrl)}" alt="Student signature" />` : ''}</div><div><strong><em>Student</em></strong></div><div><strong>${data.studentName}</strong></div></div>
  </div>
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 300); });</script>
</div></body></html>`
}
function buildSingleTermPrintHtml(
  data: SingleTermReportCardDto,
  termNum: number,
): string {
  const caTotal = (s: { firstCA: number; secondCA: number; thirdCA: number }) =>
    s.firstCA + s.secondCA + s.thirdCA

  const subjectRows = data.subjects
    .map(
      (s) => `<tr>
        <td class="subj">${s.subjectName.toUpperCase()}</td>
        <td class="c">100</td>
        <td class="c">${caTotal(s)}</td>
        <td class="c">${s.exam}</td>
        <td class="c b">${s.totalScore}</td>
        <td class="c b">${s.grade}</td>
        <td class="c">${s.remark}</td>
      </tr>`,
    )
    .join("")

  const gradingRows = GRADING_TABLE.map(
    (g) =>
      `<tr><td style="font-weight:600">${g.grade} = ${g.range}</td><td>${g.remark}</td></tr>`,
  ).join("")

  const effectivenessRows = EFFECTIVENESS_ITEMS.map(
    (item) =>
      `<tr><td>${item}</td>${[5, 4, 3, 2, 1].map(() => `<td class="c"><div class="bx"></div></td>`).join("")}</tr>`,
  ).join("")

  const resumeDate = data.nextTermBeginsOn
    ? new Date(data.nextTermBeginsOn).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : ""

  const primary = (typeof window !== 'undefined') ? getComputedStyle(document.documentElement).getPropertyValue('--primary') || '#1e3a5f' : '#1e3a5f'
  const styles = PRINT_STYLES.replace(/__PRIMARY__/g, primary.trim())

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Report Card — ${data.studentName}</title>
<style>${styles}</style></head><body>

<div class="page">
<div class="header">
  <div class="logo">${data.schoolLogo ? `<img src="${resolveMediaUrl(data.schoolLogo)}" alt="${data.schoolName} logo" />` : ``}</div>
  <div class="meta">
    <h1>${data.schoolName}</h1>
    <div class="sub">Report Sheet</div>
  </div>
  <div class="student-photo">${data.studentPhotoUrl ? `<img src="${resolveMediaUrl(data.studentPhotoUrl)}" alt="${data.studentName} profile" />` : `<span style="font-size:9px;color:#666;text-align:center;line-height:1.2;padding:4px">No photo uploaded</span>`}</div>
</div>

<table class="info">
  <tr>
    <td><span class="lbl">STUDENT'S NAME:</span> ${data.studentName}</td>
    <td><span class="lbl">SESSION:</span> ${data.session}</td>
    <td><span class="lbl">TERM:</span> ${termLabel(termNum)}</td>
    <td><span class="lbl">CLASS:</span> ${data.className}</td>
    <td><span class="lbl">PERFORMANCE:</span> ${data.percentage.toFixed(1)}%</td>
  </tr>
  <tr>
    <td><span class="lbl">NO. IN CLASS:</span> ${data.totalStudents}</td>
    <td><span class="lbl">POSITION:</span> ${formatPosition(data.position)}</td>
    <td><span class="lbl">GENDER:</span> ${data.gender}</td>
    <td colspan="2"><span class="lbl">STUDENT ID:</span> ${data.studentUin}</td>
  </tr>
</table>

<table>
  <tr><td class="sh" style="width:50%">Attendance</td><td class="sh">Weight / Height</td></tr>
</table>
<table>
  <tr>
    <td style="width:25%">No. of Times School Opened</td><td style="width:25%"></td>
    <td style="width:25%">Expected Date of Resumption</td><td style="width:25%">${resumeDate}</td>
  </tr>
  <tr>
    <td>No. of Times Present</td><td></td>
    <td>Height at the end of term</td><td></td>
  </tr>
  <tr>
    <td>No. of Times Punctual</td><td></td>
    <td>Weight at the beginning of term</td><td></td>
  </tr>
  <tr>
    <td></td><td></td>
    <td>Weight at the end of term</td><td></td>
  </tr>
</table>

<table><tr><td class="sh" colspan="7">Assessment</td></tr></table>
<table class="at">
  <thead>
    <tr>
      <th style="text-align:left">SUBJECTS</th>
      <th>Marks<br>Obtainable</th>
      <th>Continues<br>Assessment</th>
      <th>Exam<br>Score</th>
      <th>${termLabel(termNum)}<br>Score (%)</th>
      <th>Grade</th>
      <th>Remarks</th>
    </tr>
  </thead>
  <tbody>${subjectRows}</tbody>
</table>

<div class="bg">
  <div>
    <h4>Grading Method</h4>
    <table>${gradingRows}</table>
  </div>
  <div>
    <h4>Effectiveness</h4>
    <table>
      <tr><th></th><th class="c">5</th><th class="c">4</th><th class="c">3</th><th class="c">2</th><th class="c">1</th></tr>
      ${effectivenessRows}
    </table>
  </div>
</div>

<div class="cm">
  <span class="lbl">Class Teacher's Comment:</span> <em style="white-space:pre-wrap">${escapeHtml(data.classTeacherComment || '')}</em> ______________________________________________ &nbsp;&nbsp;
  <span class="lbl">Signature:</span> <span style="display:inline-flex;width:72px;height:22px;align-items:flex-end;justify-content:center;border-bottom:1px solid #999">${data.classTeacherSignatureUrl ? `<img src="${resolveMediaUrl(data.classTeacherSignatureUrl)}" style="max-height:21px;max-width:100%;object-fit:contain" alt="Class teacher signature" />` : ``}</span>
</div>
<div class="cm">
  <span class="lbl">Principal's Comment:</span> <em>${data.principalRemark}</em>
</div>
<div class="cm" style="border-bottom:1px solid #bbb"></div>

<div class="siggrid">
  <div class="in">
    <div class="sigbox">${data.principalSignatureUrl ? `<img src="${resolveMediaUrl(data.principalSignatureUrl)}" alt="Principal signature" />` : ``}</div>
    <div><strong><em>Principal</em></strong></div>
    <div><strong>${data.schoolName.toUpperCase()}</strong></div>
  </div>
  <div class="in">
    <div class="sigbox">${data.studentSignatureUrl ? `<img src="${resolveMediaUrl(data.studentSignatureUrl)}" alt="Student signature" />` : ``}</div>
    <div><strong><em>Student</em></strong></div>
    <div><strong>${data.studentName}</strong></div>
  </div>
</div>

<script>
  window.addEventListener('load', function () {
    setTimeout(function () { window.print(); }, 300);
  });
</script>

</div>

</body></html>`
}

/* ════════════════════════════════════════
   PRINT HTML — CUMULATIVE
   ════════════════════════════════════════ */

function buildCumulativePrintHtml(
  data: CumulativeReportCardDto,
): string {
  const subjectRows = data.subjects
    .map(
      (s) => `<tr>
        <td class="subj">${s.subjectName.toUpperCase()}</td>
        <td class="c">${s.firstTermTotal || "—"}</td>
        <td class="c">${s.secondTermTotal || "—"}</td>
        <td class="c">${s.thirdTermTotal || "—"}</td>
        <td class="c b">${s.averageScore.toFixed(1)}</td>
        <td class="c b">${s.grade}</td>
        <td class="c">${s.remark}</td>
      </tr>`,
    )
    .join("")

  const gradingRows = GRADING_TABLE.map(
    (g) =>
      `<tr><td style="font-weight:600">${g.grade} = ${g.range}</td><td>${g.remark}</td></tr>`,
  ).join("")

  const effectivenessRows = EFFECTIVENESS_ITEMS.map(
    (item) =>
      `<tr><td>${item}</td>${[5, 4, 3, 2, 1].map(() => `<td class="c"><div class="bx"></div></td>`).join("")}</tr>`,
  ).join("")

  const resumeDate = data.nextTermBeginsOn
    ? new Date(data.nextTermBeginsOn).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : ""

  const primary = (typeof window !== 'undefined') ? getComputedStyle(document.documentElement).getPropertyValue('--primary') || '#1e3a5f' : '#1e3a5f'
  const styles = PRINT_STYLES.replace(/__PRIMARY__/g, primary.trim())

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Cumulative Report — ${data.studentName}</title>
<style>${styles}</style></head><body>

<div class="page">
<div class="header">
  <div class="logo">${data.schoolLogo ? `<img src="${resolveMediaUrl(data.schoolLogo)}" alt="${data.schoolName} logo" />` : ``}</div>
  <div class="meta">
    <h1>${data.schoolName}</h1>
    <div class="sub">Cumulative Report Sheet — ${data.session}</div>
  </div>
  <div class="student-photo">${data.studentPhotoUrl ? `<img src="${resolveMediaUrl(data.studentPhotoUrl)}" alt="${data.studentName} profile" />` : `<span style="font-size:9px;color:#666;text-align:center;line-height:1.2;padding:4px">No photo uploaded</span>`}</div>
</div>

<table class="info">
  <tr>
    <td><span class="lbl">STUDENT'S NAME:</span> ${data.studentName}</td>
    <td><span class="lbl">SESSION:</span> ${data.session}</td>
    <td><span class="lbl">CLASS:</span> ${data.className}</td>
    <td><span class="lbl">PERFORMANCE:</span> ${data.cumulativePercentage.toFixed(1)}%</td>
  </tr>
  <tr>
    <td><span class="lbl">STUDENT ID:</span> ${data.studentUin}</td>
    <td><span class="lbl">NO. IN CLASS:</span> ${data.totalStudents}</td>
    <td><span class="lbl">POSITION:</span> ${formatPosition(data.cumulativePosition)}</td>
    <td><span class="lbl">GENDER:</span> ${data.gender}</td>
  </tr>
</table>

<table>
  <tr><td class="sh" style="width:50%">Attendance</td><td class="sh">Weight / Height</td></tr>
</table>
<table>
  <tr>
    <td style="width:25%">No. of Times School Opened</td><td style="width:25%"></td>
    <td style="width:25%">Expected Date of Resumption</td><td style="width:25%">${resumeDate}</td>
  </tr>
  <tr>
    <td>No. of Times Present</td><td></td>
    <td>Height at the end of term</td><td></td>
  </tr>
  <tr>
    <td>No. of Times Punctual</td><td></td>
    <td>Weight at the beginning of term</td><td></td>
  </tr>
  <tr>
    <td></td><td></td>
    <td>Weight at the end of term</td><td></td>
  </tr>
</table>

<table><tr><td class="sh" colspan="7">Assessment — Cumulative</td></tr></table>
<table class="at">
  <thead>
    <tr>
      <th style="text-align:left">SUBJECTS</th>
      <th>1st Term<br>Total</th>
      <th>2nd Term<br>Total</th>
      <th>3rd Term<br>Total</th>
      <th>Average</th>
      <th>Grade</th>
      <th>Remarks</th>
    </tr>
  </thead>
  <tbody>${subjectRows}</tbody>
</table>

<div class="bg">
  <div>
    <h4>Grading Method</h4>
    <table>${gradingRows}</table>
  </div>
  <div>
    <h4>Effectiveness</h4>
    <table>
      <tr><th></th><th class="c">5</th><th class="c">4</th><th class="c">3</th><th class="c">2</th><th class="c">1</th></tr>
      ${effectivenessRows}
    </table>
  </div>
<div class="cm">
  <span class="lbl">Class Teacher's Comment:</span> <em style="white-space:pre-wrap">${escapeHtml(data.classTeacherComment || '')}</em> ______________________________________________ &nbsp;&nbsp;
  <span class="lbl">Signature:</span> <span style="display:inline-flex;width:72px;height:22px;align-items:flex-end;justify-content:center;border-bottom:1px solid #999">${data.classTeacherSignatureUrl ? `<img src="${resolveMediaUrl(data.classTeacherSignatureUrl)}" style="max-height:21px;max-width:100%;object-fit:contain" alt="Class teacher signature" />` : ``}</span>
</div>
<div class="cm">
  <span class="lbl">Principal's Comment:</span> <em>${data.principalRemark}</em>
</div>
<div class="cm" style="border-bottom:1px solid #bbb"></div>

<div class="siggrid">
  <div class="in">
    <div class="sigbox">${data.principalSignatureUrl ? `<img src="${resolveMediaUrl(data.principalSignatureUrl)}" alt="Principal signature" />` : ``}</div>
    <div><strong><em>Principal</em></strong></div>
    <div><strong>${data.schoolName.toUpperCase()}</strong></div>
  </div>
  <div class="in">
    <div class="sigbox">${data.studentSignatureUrl ? `<img src="${resolveMediaUrl(data.studentSignatureUrl)}" alt="Student signature" />` : ``}</div>
    <div><strong><em>Student</em></strong></div>
    <div><strong>${data.studentName}</strong></div>
  </div>
</div>

<script>
  window.addEventListener('load', function () {
    setTimeout(function () { window.print(); }, 300);
  });
</script>

</div>

</body></html>`
}
